import { useEffect, useRef, useState } from 'preact/hooks'
import type { ApiResponse } from '@/api/client'
import type { NonProspectDomainCsvPreviewDto, NonProspectDomainsViewDto } from '@/types/nonProspectDomains'
import { getDisplayErrorMessages } from '@/utils/errorHandling'
import { domainSaveError, type NonProspectDomainsEditor } from './useNonProspectDomainsEditor'

export type PreviewNonProspectDomains = (file: File, column?: number) => Promise<ApiResponse<NonProspectDomainCsvPreviewDto>>
export function useNonProspectDomainImport(view: NonProspectDomainsViewDto, editor: NonProspectDomainsEditor, onPreview?: PreviewNonProspectDomains) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<NonProspectDomainCsvPreviewDto | null>(null)
  const [step, setStep] = useState<'column' | 'review'>('column')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const request = useRef(0)
  useEffect(() => () => { request.current++ }, [])
  const cancel = () => {
    if (editor.saving) return
    request.current++
    setFile(null); setPreview(null); setError(''); setLoading(false); setStep('column'); editor.clearError()
  }
  const choose = async (next: File, column?: number) => {
    if (!onPreview || editor.saving) return
    const mine = ++request.current
    setFile(next); setPreview(null); setStep('column'); setError(''); editor.clearError()
    if (!/\.csv$/i.test(next.name) || next.size > 2 * 1024 * 1024) {
      setLoading(false); setError('Choose a CSV file no larger than 2 MB.'); return
    }
    setLoading(true)
    try {
      const response = await onPreview(next, column)
      if (request.current !== mine) return
      if (!response.data || response.error || response.status >= 400) {
        setError(domainSaveError(response, 'The CSV could not be read. Try again.')); return
      }
      if (response.data.ETag !== view.ETag) {
        setError('This list changed elsewhere. Cancel the import, reload the list, and choose the CSV again.'); return
      }
      setPreview(response.data)
    } catch (cause) {
      if (request.current === mine) setError(getDisplayErrorMessages(cause, 'The CSV could not be read. Try again.').join(' '))
    } finally { if (request.current === mine) setLoading(false) }
  }
  const confirm = async () => {
    if (!preview || preview.ExceedsLimit || !preview.DomainsToAdd.length || loading || editor.saving) return
    if (await editor.persist([...view.NonProspectDomains, ...preview.DomainsToAdd], preview.ETag)) {
      request.current++; setFile(null); setPreview(null); setStep('column'); setError('')
    }
  }
  return { file, preview, step, error, loading, choose, cancel, confirm, setStep }
}
export type NonProspectDomainImportState = ReturnType<typeof useNonProspectDomainImport>
